// Qué puede medir esta visita, y quién lo decidió.
//
// Google Tag Manager no pregunta nada por su cuenta: carga las etiquetas y
// mide. El modo de consentimiento es la puerta que se le pone delante —cuatro
// señales que el contenedor consulta antes de escribir una cookie o mandar un
// dato a Google Ads—. Este módulo es lo que mueve esas señales cuando el
// visitante responde el aviso.
//
// El estado inicial no se decide acá sino en el <head> del layout, antes de que
// cargue el contenedor: una vez que gtm.js corrió, lo que llegue después ya es
// tarde para las etiquetas que se dispararon al abrir la página. Ese script
// lee la misma llave de localStorage que se escribe acá, así que la decisión
// sobrevive a la recarga y el aviso no vuelve a aparecer.

import { ref } from 'vue'

export const CONSENT_KEY = 'casa_lumina_consent'

// La decisión, una sola para toda la página. El aviso de abajo y los botones de
// la política de privacidad son dos componentes distintos que hablan de lo
// mismo: si cada uno guardara su propia copia, aceptar en el aviso dejaría a la
// política diciendo "todavía no has respondido" hasta la siguiente recarga.
//
// Empieza en null —sin respuesta— y no en lo guardado, porque acá todavía no
// hay navegador. Lo llena loadConsent() cuando el primer componente se monta.
export const consent = ref(null)

export function loadConsent () {
  consent.value = readConsent()

  return consent.value
}

// Las cuatro señales que usa Google. Van juntas y con el mismo valor a
// propósito: el aviso hace una sola pregunta, así que no hay de dónde sacar un
// sí para unas y un no para otras. Separarlas sería inventar una respuesta que
// el visitante no dio.
export const CONSENT_SIGNALS = [
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
  'analytics_storage'
]

export function consentState (value) {
  return Object.fromEntries(CONSENT_SIGNALS.map((signal) => [ signal, value ]))
}

// La decisión guardada, o null si todavía no hay ninguna —que es lo único que
// hace aparecer el aviso—. Cualquier otra cosa escrita en esa llave se trata
// como si no hubiera decisión: vale más volver a preguntar que adivinar.
//
// localStorage puede lanzar, no solo venir vacío: en una ventana privada o con
// las cookies bloqueadas, leerlo es un error. Ahí no hay dónde recordar la
// respuesta, así que el aviso reaparece cada visita; molesto, pero es la única
// lectura honesta.
export function readConsent () {
  try {
    const stored = globalThis.localStorage?.getItem(CONSENT_KEY)

    return stored === 'granted' || stored === 'denied' ? stored : null
  } catch {
    return null
  }
}

// El dataLayer recibe los argumentos crudos, no un objeto con nombres. Así es
// como Google definió gtag() y como el contenedor los espera: una función
// normal y no una flecha, porque lo que se empuja es su `arguments`.
function gtag () {
  const layer = globalThis.window.dataLayer = globalThis.window.dataLayer || []

  layer.push(arguments)
}

// Avisa al contenedor sin recargar. Si no se avisara, aceptar no serviría de
// nada hasta la página siguiente, y quien acepta y cotiza en la misma visita
// —que es el camino normal— no se mediría nunca.
export function updateConsent (value) {
  if (!globalThis.window) return

  gtag('consent', 'update', consentState(value))
}

// Guarda la respuesta y la aplica en el acto. Devuelve lo que guardó, o null si
// le pidieron guardar algo que no es una respuesta.
export function saveConsent (value) {
  if (value !== 'granted' && value !== 'denied') return null

  try {
    globalThis.localStorage?.setItem(CONSENT_KEY, value)
  } catch {
    // Sin dónde guardar, la decisión igual vale para esta visita.
  }

  consent.value = value
  updateConsent(value)

  return value
}
